import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '@/components/elements/Button';
import { RxCross1 } from 'react-icons/rx';
import { motion, AnimatePresence } from 'framer-motion';
import type { MouseEvent, ReactNode, ReactElement } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children?: ReactNode;
  className?: string;
  'data-cy'?: string; // Keep this as is in the interface
  buttons?: Array<ReactElement<typeof Button>>;
}

function Modal({ isOpen, onClose, title, children, className, 'data-cy': dataCy, buttons }: ModalProps) {
  const transitionDurationMs = 250;
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (onClose && event.key === 'Escape') {
        onClose();
      }
    }

    if (onClose) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      if (onClose) window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  function handleBackdropClick(event: MouseEvent) {
    if (onClose) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className='p-4 fixed inset-0 bg-black bg-opacity-50 flex backdrop-blur-sm items-center justify-center z-50'
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: transitionDurationMs / 1000 }}
        >
          <motion.dialog
            open
            data-cy={dataCy}
            ref={dialogRef}
            aria-labelledby='modal-title'
            className={`relative bg-transparent bg-white rounded-lg shadow-lg max-h-[calc(100vh-2rem)] overflow-auto p-6 m-0 max-w-[500px] ${className ? className : ''}`}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: transitionDurationMs / 1000 }}
          >
            {onClose && (
              <button
                onClick={onClose}
                className='absolute w-[34px] h-[34px] flex items-center justify-center right-4 top-4 p-2 rounded-md hover:bg-gray-100 transition-colors'
                aria-label='Close dialog'
              >
                <RxCross1 className='w-5 h-5' />
              </button>
            )}

            {title && (
              <header className='pr-8'>
                <h2 className='text-lg font-semibold mb-1' id='modal-title'>
                  {title}
                </h2>
              </header>
            )}

            <main className='my-4'>{children}</main>

            {buttons && buttons.length > 0 && (
              <footer className='grid grid-cols-2 gap-3 mt-6'>
                {buttons.map((button, index) => (
                  <div key={index} className='[&>button]:!w-full'>
                    {button}
                  </div>
                ))}
              </footer>
            )}
          </motion.dialog>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default Modal;
